// React imports
import { useState, useEffect, useMemo } from 'react';

// Generic imports
import dayjs from 'dayjs';
import axios from 'axios';
import _ from 'lodash';
import pluralize from 'pluralize';

// MUI imports
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faDownload, faArrowsRotate, faFileCsv } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { RatesMain } from './Rates.js';
import { FormControl, FormControlLabel } from '@mui/material';
import { generateRateSignature } from '../utils/rateSignature.js';
import { ratesToMonadaCsv } from './utils.js';

function CreateImport(props) {
    const { typeTitle, onNext, onBack, form, organizationId, accesstoken } = props;
    const [ errors, setErrors ] = useState([]);
    const [ validFrom, setValidFrom] = useState(form?.valid_from ? dayjs(form.valid_from) : dayjs());
    const [ validUntil, setValidUntil] = useState(form?.valid_to ? dayjs(form.valid_to) : dayjs());
    const [ supplier, setSupplier] = useState(form?.supplier ? parseInt(form.supplier) : '');
    const [ shipmentType, setShipmentType] = useState(form?.shipment_type_id ? parseInt(form.shipment_type_id) : 'FCL');
    const [ cargofiveProviders, setCargofiveProviders ] = useState(null);
    const [ loadingProviders, setLoadingProviders ] = useState(true);
    const [ errorLoadingProviders, setErrorLoadingProviders ] = useState(null);
    const [ errorTimestamp, setErrorTimestamp ] = useState(null);
    const [ retryCountdown, setRetryCountdown ] = useState(0);

    async function fetchCargofiveProviders() {
        setLoadingProviders(true);
        setErrorLoadingProviders(null);
        setErrorTimestamp(null);
        setRetryCountdown(0);
        try {
            const { data: _cargofiveProviders } = await axios.get(`/api/cargofive/suppliers?organizationId=${organizationId}&accesstoken=${accesstoken}`);
            setCargofiveProviders(_cargofiveProviders.data);
            setErrorLoadingProviders(null);
            setErrorTimestamp(null);
            setRetryCountdown(0);
        } catch (e) {
            console.log('[RatesImport] Error getting cargofive providers', e);
            setErrorLoadingProviders(e);
            setErrorTimestamp(Date.now());
            setRetryCountdown(30);
        } finally {
            setLoadingProviders(false);
        }
    }

    useEffect(() => {
        fetchCargofiveProviders();
    }, []);

    useEffect(() => {
        if (errorTimestamp) {
            const interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - errorTimestamp) / 1000);
                const remaining = Math.max(0, 30 - elapsed);
                setRetryCountdown(remaining);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [errorTimestamp]);

    function _onNext() {
        const errors = [];
        if (!_.trim(supplier)) errors.push('supplier');
        if (validFrom.valueOf() >= validUntil.valueOf()) errors.push('dates');
        setErrors(errors);
        if (errors.length > 0) return;

        const _supplier = cargofiveProviders.find(p => p.id === parseInt(supplier));
        const form = {
            file_type_id: 1,
            shipment_type_id: shipmentType,
            reference: 'Reference',
            remarks: 'Remarks',
            supplier: _supplier.id,
            // The name as well as the id: CargoFive is addressed by provider id, but the
            // import record needs something readable, and the id means nothing outside
            // their system.
            supplier_name: _supplier.name || _supplier.title || '',
            valid_from: validFrom.format('YYYY-MM-DD'),
            valid_to: validUntil.format('YYYY-MM-DD'),
            source_id: 1
        }

        onNext({ form });
    }

    if (loadingProviders && !errorLoadingProviders) {
        return (
            <Box style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', paddingTop: '36px' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (errorLoadingProviders) {
        return (
            <Dialog open={true} onClose={loadingProviders ? undefined : onBack} maxWidth='sm' fullWidth>
                <DialogTitle>
                    <Typography variant='h6'>
                        Error Loading Providers
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {loadingProviders ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Alert severity='error' sx={{ mb: 2 }}>
                                Failed to load CargoFive providers due to quota warning. Please wait before retrying.
                            </Alert>
                            {retryCountdown > 0 ? (
                                <Typography variant='body2' sx={{ mb: 2 }}>
                                    You can retry in <strong>{retryCountdown}</strong> {pluralize('second', retryCountdown)}.
                                </Typography>
                            ) : (
                                <Typography variant='body2' sx={{ mb: 2, color: 'success.main' }}>
                                    You can now retry the request.
                                </Typography>
                            )}
                            {errorLoadingProviders?.response?.data?.message && (
                                <Typography variant='body2' color='error' sx={{ mb: 2 }}>
                                    {errorLoadingProviders.response.data.message}
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onBack} color='secondary' disabled={loadingProviders}>
                        Back
                    </Button>
                    <Button onClick={fetchCargofiveProviders} variant='contained' color='primary' disabled={loadingProviders || retryCountdown > 0}>
                        {loadingProviders ? <CircularProgress size={20} /> : retryCountdown > 0 ? `Retry (${retryCountdown}s)` : 'Retry'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Card sx={{ ...style, width: '560px'}}>
            <CardContent>
                <Typography gutterBottom variant='h5' component='div'>
                    Get your rates from <b>{typeTitle}</b>
                </Typography>
                <Select sx={{ mb: 2 }} id='shipment_type_id' label='Shipment type' value={shipmentType} onChange={e => setShipmentType(e.target.value)} fullWidth>
                    <MenuItem value='1'>FCL</MenuItem>
                    <MenuItem value='2'>LCL</MenuItem>
                    <MenuItem value='3'>FTL Location</MenuItem>
                    <MenuItem value='4'>FTL Range</MenuItem>
                    <MenuItem value='7'>Charge FCL</MenuItem>
                    <MenuItem value='8'>Charge LCL</MenuItem>
                </Select>
                <Autocomplete
                    id='reference'
                    label='Supplier (organization)'
                    value={supplier}
                    onChange={(e, newValue) => {
                        setSupplier(parseInt(newValue));
                    }}
                    fullWidth
                    options={cargofiveProviders?.map(p => p.id)}
                    renderInput={(params) => <TextField {...params} error={errors.includes('supplier')} />}
                    getOptionLabel={option => {
                        const p = cargofiveProviders.find(p => p.id === option);
                        if (!p) return '';
                        return `${p.id} - ${p.name} ${p.delegation_name ? `(${p.delegation_name})` : ''}`;
                    }}
                    sx={{ mb: 2 }}
                />
                <MobileDatePicker
                    label='Valid From (DD/MM/YYYY)'
                    inputFormat='DD/MM/YYYY'
                    value={validFrom}
                    onChange={(newValue) => setValidFrom(newValue)}
                    renderInput={(params) => <TextField fullWidth {...params} sx={{ ...(params.sx || {}), mb: 2 }} error={errors.includes('dates')} />}
                />
                <MobileDatePicker
                    label='Valid Until (DD/MM/YYYY)'
                    inputFormat='DD/MM/YYYY'
                    value={validUntil}
                    onChange={(newValue) => setValidUntil(newValue)}
                    renderInput={(params) => <TextField fullWidth {...params} sx={{ ...(params.sx || {}), mb: 2 }} error={errors.includes('dates')} />}
                />
            </CardContent>
            <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button size='small' color='secondary' onClick={onBack}>
                    Back
                </Button>
                <Button size='small' color='primary' variant='contained' onClick={_onNext}>
                    Continue to upload file
                </Button>
            </CardActions>
        </Card>
    );
}

async function apiCreateRequest({ form, file, fileName, org, accesstoken }) {
    const formData = new FormData();
    const blob = await (await fetch(file)).blob();
    formData.append('file', blob, fileName);
    formData.append('file_type_id', 1);
    formData.append('shipment_type_id', form.shipment_type_id);
    formData.append('reference', form.reference);
    formData.append('remarks', form.remarks);
    formData.append('provider_ids', JSON.stringify([form.supplier]));
    if (form.supplier_name) formData.append('supplier_name', form.supplier_name);
    formData.append('valid_from', form.valid_from);
    formData.append('valid_to', form.valid_to);
    formData.append('source_id', form.source_id);
    await axios.post(`/api/cargofive?organizationId=${org.id}&accesstoken=${accesstoken}`, formData);
}

function PublishedRatesView(props) {
    const { rates: initialRates, onClose, accesstoken, organization, ratesImport, onUpdateRatesImport } = props;
    const [ rates, setRates ] = useState(initialRates);
    const [ transformedRates, setTransformedRates ] = useState(null);
    const [ loading, setLoading ] = useState(null);
    const [ message, setMessage ] = useState(null);
    const [ forceSaving, setForceSaving ] = useState([]);
    const [ forceSavingSuccess, setForceSavingSuccess ] = useState([]);
    const [ forceSavingError, setForceSavingError ] = useState([]);
    const [ existingRates, setExistingRates ] = useState(null);
    const [ overrideExistingRates, setOverrideExistingRates ] = useState(false);
    const [ supplierOverride, setSupplierOverride ] = useState('');

    // Re-file every parsed rate under a supplier typed here, before uploading.
    // The supplier is part of a rate's identity — buildSignatureInput() hashes it
    // (utils/rateSignature.js) and cargofiveId embeds it — so rewriting it makes these
    // rates new records rather than updates of the ones already published. Changing
    // `rates` re-runs the duplicate check via the existing effect, so the counts and
    // the Add/Override buttons settle themselves.
    function onApplySupplier() {
        const organization = supplierOverride.trim();
        if (!organization) return;
        // Mirrors normalizeCode() in utils/cargofive.js, which built the original id.
        const normalized = String(organization).toUpperCase()
            .replace(/\u201c|\u201d/g, '"').replace(/[\u2019`]/g, "'").replace(/["']/g, '').trim();
        setRates(prev => prev.map(r => {
            const parts = String(r.attributes?.cargofiveId || '').split('|');
            // cargofiveId is `lane|product|supplier|via|HAZ`; keep it in step with the rename.
            if (parts.length === 5) parts[2] = normalized;
            return {
                ...r,
                supplier: { ...(r.supplier || {}), organization },
                attributes: {
                    ...(r.attributes || {}),
                    ...(parts.length === 5 ? { cargofiveId: parts.join('|') } : {}),
                },
            };
        }));
    }

    const products = useMemo(() => {
        const pairs = ['air', 'sea', 'land'].map(transportationMethod => ([transportationMethod, (transformedRates || rates).filter(r => r.transportationMethod === transportationMethod).map(r => r.product)]));
        return _.fromPairs(pairs);
    }, [rates, transformedRates]);

    async function resetExisting() {
        setTransformedRates(null);
        setExistingRates(null);
        if (!rates || rates.length === 0) {
            setExistingRates([]);
            return;
        }
        setLoading('Checking for duplicate rates...');
        try {
            // Generate signatures for all imported rates
            const rateSignatures = await Promise.all(
                rates.map(async (r) => {
                    const signature = await generateRateSignature(r);
                    return { rate: r, signature };
                })
            );

            // Build signature -> rate mapping and signature array
            const signatureToRate = new Map();
            const signatures = [];
            for (const { rate, signature } of rateSignatures) {
                if (signature) {
                    signatureToRate.set(signature, rate);
                    signatures.push(signature);
                    // Also store the signature on the rate for later matching
                    rate.attributes = rate.attributes || {};
                    rate.attributes.signature = signature;
                }
            }

            const { data: { duplicates } } = await axios.post(
                `/api/cargofive/${ratesImport.id}/check-duplicates?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                { signatures }
            );

            // Convert duplicates map to array of rate-like objects for compatibility
            const existingRatesList = Object.entries(duplicates).map(([signature, info]) => ({
                id: info.id,
                attributes: { signature },
                supplier: { organization: info.supplier },
                offer: { validFrom: info.validFrom, validUntil: info.validUntil },
            }));
            setExistingRates(existingRatesList);
        } catch (e) {
            console.error('[RatesImport] Error checking duplicates', e);
            setExistingRates([]);
        }
        setLoading(null);
    }

    function onDeleteOffer({ offerId }) {
        resetExisting();
    }

    useEffect(() => {
        resetExisting();
    }, [rates]);

    // Helper function to match rates by signature hash
    // Signatures are computed from: source, destination, product, supplier,
    // validFrom, validUntil, type, transitTime, transshipment, and all sections/fields
    function matchesRate(existingRate, newRate) {
        const existingSignature = existingRate.attributes?.signature;
        const newSignature = newRate.attributes?.signature;

        // If both have signatures, compare directly
        if (existingSignature && newSignature) {
            return existingSignature === newSignature;
        }

        // Fallback to legacy cargofiveId matching for backward compatibility
        const existingId = existingRate.attributes?.cargofiveId;
        const newId = newRate.attributes?.cargofiveId;
        if (!existingId || !newId) return false;

        // Exact match (both use new format or both use old format)
        if (existingId === newId) {
            // Double-check supplier matches to prevent cross-supplier collisions
            const existingSupplier = existingRate.supplier?.organization || '';
            const newSupplier = newRate.supplier?.organization || '';
            if (existingSupplier && newSupplier && existingSupplier.toLowerCase() !== newSupplier.toLowerCase()) {
                return false;
            }
            // Also check validity dates match
            const existingValidFrom = existingRate.offer?.validFrom || '';
            const existingValidUntil = existingRate.offer?.validUntil || '';
            const newValidFrom = newRate.offer?.validFrom || '';
            const newValidUntil = newRate.offer?.validUntil || '';
            if (existingValidFrom !== newValidFrom || existingValidUntil !== newValidUntil) {
                return false;
            }
            return true;
        }

        // Backward compatibility: check if old format matches new format base
        const existingParts = existingId.split('|');
        const newParts = newId.split('|');

        if (existingParts.length === 2 && newParts.length === 4) {
            if (existingParts[0] === newParts[0] && existingParts[1] === newParts[1]) {
                const existingSupplier = existingRate.supplier?.organization || '';
                const newSupplier = newRate.supplier?.organization || '';
                const isSameSupplier = existingSupplier && newSupplier && existingSupplier.toLowerCase() === newSupplier.toLowerCase();

                const existingValidFrom = existingRate.offer?.validFrom || '';
                const existingValidUntil = existingRate.offer?.validUntil || '';
                const newValidFrom = newRate.offer?.validFrom || '';
                const newValidUntil = newRate.offer?.validUntil || '';
                const isSameValidity = existingValidFrom === newValidFrom && existingValidUntil === newValidUntil;

                return isSameSupplier && isSameValidity;
            }
        }

        return false;
    }

    // Helper function to check if validity dates have changed
    function validityChanged(existingRate, newRate) {
        const existingValidFrom = existingRate.offer?.validFrom || '';
        const existingValidUntil = existingRate.offer?.validUntil || '';
        const newValidFrom = newRate.offer?.validFrom || '';
        const newValidUntil = newRate.offer?.validUntil || '';
        
        return existingValidFrom !== newValidFrom || existingValidUntil !== newValidUntil;
    }

    // Find which imported rates have duplicates in the database
    const duplicateInfo = useMemo(() => {
        if (!existingRates || !rates) return { duplicates: [], duplicateIds: [] };

        const duplicates = [];
        const duplicateIds = [];

        for (const importedRate of rates) {
            const existingRate = existingRates.find(e => matchesRate(e, importedRate));
            if (existingRate) {
                duplicates.push({ importedRate, existingRate });
                duplicateIds.push(importedRate.id);
            }
        }

        return { duplicates, duplicateIds };
    }, [existingRates, rates]);

    const alreadyAdded = useMemo(() => {
        return duplicateInfo.duplicates.map(d => d.existingRate);
    }, [duplicateInfo]);

    useEffect(() => {
        if (!existingRates || !rates) {
            setMessage(null);
            return;
        }

        const numDuplicates = duplicateInfo.duplicates.length;
        const numNew = rates.length - numDuplicates;

        setMessage([
            numDuplicates > 0 && numNew > 0 ? `Found ${numDuplicates} ${pluralize('rate', numDuplicates)} already uploaded.` : null,
            numNew > 0 ? `${numNew} ${pluralize('rate', numNew)} to add.` : null,
            numNew === 0 ? 'All rates already uploaded.' : null
        ].filter(Boolean).join(' '));

        // Mark duplicates with green checkmark when not overriding
        // Use the IMPORTED rate IDs since those are what we display
        setForceSavingSuccess(overrideExistingRates ? [] : duplicateInfo.duplicateIds);

        // Always show the imported rate data (for proper display)
        // When overriding: keep imported data, use existing ID for updates
        // When not overriding: keep imported data, mark duplicates as success
        if (overrideExistingRates) {
            setTransformedRates(rates.map(r => {
                const match = duplicateInfo.duplicates.find(d => d.importedRate.id === r.id);
                return match ? { ...r, id: match.existingRate.id } : r;
            }));
        } else {
            // Show all imported rates as-is, duplicates will be shown with green checkmark
            setTransformedRates([...rates]);
        }
    }, [rates, existingRates, duplicateInfo, overrideExistingRates]);

    async function onPublish() {
        try {
            setLoading('Publishing rates...');
            const { data: updatedImport } = await axios.put(`/api/rates/import/${ratesImport.id}/status?organizationId=${organization.id}&accesstoken=${accesstoken}`, { status: 'published' });
            onUpdateRatesImport({ ratesImport: updatedImport });
            setLoading(null);
        } catch (e) {
            console.log('[RatesImport] Error publishing rates', e);
            setLoading(null);
        }
    }

    async function onDownloadCharges() {
        try {
            setLoading('Downloading charges...');
            const { data } = await axios.get(`/api/cargofive/${ratesImport.id}/cached-charges?organizationId=${organization.id}&accesstoken=${accesstoken}`);
            if (!data.cached) {
                setMessage('Charges not yet cached. Please wait for the import to be processed.');
                setLoading(null);
                return;
            }
            const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `cargofive-charges-${ratesImport.id}.json`;
            link.click();
            URL.revokeObjectURL(url);
            setLoading(null);
        } catch (e) {
            console.log('[RatesImport] Error downloading charges', e);
            setMessage('Error downloading charges.');
            setLoading(null);
        }
    }

    function onDownloadCsv() {
        try {
            const ratesForCsv = (transformedRates && transformedRates.length) ? transformedRates : rates;
            if (!ratesForCsv || !ratesForCsv.length) {
                setMessage('No rates to download yet.');
                return;
            }
            const csv = ratesToMonadaCsv({ rates: ratesForCsv, org: organization });
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `monada-rates-${ratesImport.id}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.log('[RatesImport] Error downloading rates CSV', e);
            setMessage('Error downloading rates CSV.');
        }
    }

    async function onRefreshCache() {
        try {
            setLoading('Refreshing charges from CargoFive...');
            const { data } = await axios.post(`/api/cargofive/${ratesImport.id}/refresh-cache?organizationId=${organization.id}&accesstoken=${accesstoken}`);

            // Convert the refreshed charges to rates
            setLoading('Converting refreshed charges...');
            const { data: convertResult } = await axios.post(`/api/cargofive/${ratesImport.id}/convert?accesstoken=${accesstoken}&organizationId=${organization.id}`);

            // Update local rates state and reset duplicate checking
            setRates(convertResult.rates);
            setForceSavingSuccess([]);
            setForceSavingError([]);
            setMessage(`Successfully refreshed ${data.chargesCount} charges (${convertResult.rates.length} rates) from CargoFive.`);
            setLoading(null);
        } catch (e) {
            console.log('[RatesImport] Error refreshing cache', e);
            setMessage(e.response?.data?.detail || 'Error refreshing charges cache.');
            setLoading(null);
        }
    }

    async function onLoad() {
        setMessage(null);
        setLoading('Adding new rates...');

        // Separate rates into new ones and existing ones
        const ratesToAdd = [];
        const skippedIds = [];

        // Create a set of duplicate imported rate IDs for quick lookup
        const duplicateImportedIds = new Set(duplicateInfo.duplicateIds);

        for (const r of transformedRates) {
            const isDuplicate = duplicateImportedIds.has(r.id) ||
                duplicateInfo.duplicates.some(d => d.existingRate.id === r.id);

            if (isDuplicate && !overrideExistingRates) {
                // Skip duplicates when not overriding - they're already in the DB
                skippedIds.push(r.id);
                continue;
            }

            // Add rate (transformedRates already has correct IDs for override mode)
            ratesToAdd.push(r);
        }

        setForceSavingSuccess(prev => [...prev, ...skippedIds]);
        setForceSaving(ratesToAdd.map(r => r.id));

            let error = false;
        if (ratesToAdd.length > 0) {
            const CHUNK_SIZE = 25;
            for (let i = 0; i < ratesToAdd.length; i += CHUNK_SIZE) {
                const chunk = ratesToAdd.slice(i, i + CHUNK_SIZE);
                try {
                    setLoading(`Adding rates... (${i + 1}\u2013${Math.min(i + chunk.length, ratesToAdd.length)} of ${ratesToAdd.length})`);
                    const { data: result } = await axios.post(
                        `/api/rates/bulk?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                        { rates: chunk }
                    );
                    setForceSavingSuccess(prev => [...prev, ...result.successful.map(s => chunk[s.index]?.id || s.id)]);
                    if (result.failed.length > 0) {
                        error = true;
                        setForceSavingError(prev => [...prev, ...result.failed.map(f => f.id)]);
                    }
                } catch (e) {
                    console.error('[RatesImport] Error bulk adding rates chunk', e);
                    error = true;
                    setForceSavingError(prev => [...prev, ...chunk.map(r => r.id)]);
                }
            }
        }

        setForceSaving([]);

        if (!error) {
            const { data: updatedImport } = await axios.put(`/api/rates/import/${ratesImport.id}/status?organizationId=${organization.id}&accesstoken=${accesstoken}`, { status: 'published' });
            onUpdateRatesImport({ ratesImport: updatedImport });
        }

        setLoading(null);
        // Re-check duplicates to update UI state
        await resetExisting();
    }

    return (
        <Dialog open={true} onClose={onClose} maxWidth='xl' fullWidth>
            <DialogTitle>
                {loading ? (
                    <Alert severity='info' sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
                        {loading}
                    </Alert>
                ) : (
                    <></>
                )}
                {message ? (
                    <Alert severity='info' sx={{ mb: 2 }}>
                        {message}
                        {(alreadyAdded?.length > 0) && (
                            <FormControlLabel
                                sx={{ ml: 2 }}
                                control={<Checkbox checked={overrideExistingRates} onChange={e => setOverrideExistingRates(e.target.checked)} />}
                                label={<Typography variant='body2'>Override existing rates</Typography>}
                            />
                        )}
                    </Alert>
                ) : (
                    <></>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6' sx={{ flex: 1 }}>
                        Rates
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                        <TextField
                            size='small'
                            label='Supplier'
                            placeholder='Re-file all rates under…'
                            value={supplierOverride}
                            disabled={loading}
                            onChange={e => setSupplierOverride(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') onApplySupplier(); }}
                            sx={{ width: 260 }}
                        />
                        <Button
                            variant='outlined'
                            color='primary'
                            disabled={loading || !supplierOverride.trim()}
                            onClick={onApplySupplier}
                            title='Set this supplier on every rate below, then use Add to Rates to upload them'
                        >
                            Apply to all
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        {alreadyAdded?.length >= rates?.length && !overrideExistingRates && ratesImport?.status !== 'published' && (
                            <Button variant='contained' color='success' disabled={loading} onClick={onPublish}>
                                Publish
                            </Button>
                        )}
                        <Button
                            variant='contained'
                            color='primary'
                            disabled={loading || !existingRates || !transformedRates || (alreadyAdded?.length >= rates?.length && !overrideExistingRates)}
                            onClick={onLoad}
                        >
                            {overrideExistingRates ? 'Override Rates' : 'Add to Rates'}
                        </Button>
                        <Button onClick={onRefreshCache} color='secondary' disabled={loading} title='Refresh charges cache from CargoFive'>
                            <FontAwesomeIcon icon={faArrowsRotate} />
                        </Button>
                        <Button onClick={onDownloadCsv} color='secondary' disabled={loading} title='Download rates as Monada CSV'>
                            <FontAwesomeIcon icon={faFileCsv} />
                        </Button>
                        <Button onClick={onDownloadCharges} color='secondary' disabled={loading} title='Download original CargoFive charges JSON'>
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                        <Button onClick={onClose} color='error' disabled={loading}>
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </Box>
                </Box>

            </DialogTitle>
            <DialogContent>
                {transformedRates && (
                    <RatesMain
                        mode='rates-import'
                        rates={transformedRates}
                        setRates={null}
                        base={null}
                        setBase={null}
                        products={products}
                        noHeader={true}
                        forceTransportationMethod='sea'
                        forceSaving={forceSaving}
                        forceSavingSuccess={forceSavingSuccess}
                        forceSavingError={forceSavingError}
                        onDeleteOffer={onDeleteOffer}
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}

export { CreateImport, apiCreateRequest, PublishedRatesView };

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};